import { useState } from "react";
import {
  Lock,
  ShieldCheck,
  ArrowRight,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ResetPassword() {
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const rules = [
    { label: "At least 8 characters", ok: password.length >= 8 },
    { label: "One uppercase letter", ok: /[A-Z]/.test(password) },
    { label: "One lowercase letter", ok: /[a-z]/.test(password) },
    { label: "One number", ok: /[0-9]/.test(password) },
    { label: "One special character", ok: /[^A-Za-z0-9]/.test(password) },
  ];

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (!/[A-Z]/.test(password)) return setError("Password must include at least one uppercase letter.");
    if (!/[a-z]/.test(password)) return setError("Password must include at least one lowercase letter.");
    if (!/[0-9]/.test(password)) return setError("Password must include at least one number.");
    if (!/[^A-Za-z0-9]/.test(password)) return setError("Password must include at least one special character.");
    if (password !== confirmPassword) return setError("Passwords do not match.");

    const userId = localStorage.getItem("resetUserId");
    if (!userId) return setError("Session expired. Please try again.");

    try {
      setLoading(true);
      const res = await axios.post(`${API_URL}/api/auth/reset-password`, { userId, password });
      if (res.data.success) {
        setSuccess("Password updated successfully.");
        localStorage.removeItem("resetUserId");
        setTimeout(() => navigate("/"), 1500);
      } else {
        setError(res.data.message || "Unable to update password.");
      }
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          "Unable to update password."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-5">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-xl">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-xl bg-blue-100">
          <ShieldCheck className="text-blue-600" size={24} />
        </div>

        <h2 className="text-center text-3xl font-bold text-slate-900">Reset Password</h2>
        <p className="mt-2 mb-6 text-center text-slate-500">
          Create a strong new password for your account.
        </p>

        {error && (
          <div data-testid="reset-error" className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div data-testid="reset-success" className="mb-5 flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-600">
            <CheckCircle2 size={18} />
            <span>{success}</span>
          </div>
        )}

        <form onSubmit={handleReset} className="space-y-5">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">New Password</label>
            <div className="flex items-center rounded-lg border border-slate-300 bg-white transition focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-100">
              <Lock className="ml-3 text-slate-400" size={18} />
              <input
                data-testid="reset-password-input"
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password"
                className="w-full bg-transparent px-3 py-3 outline-none"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="px-3 text-slate-500 hover:text-blue-600"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {password && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
              {rules.map((r) => (
                <li
                  key={r.label}
                  className={`flex items-center gap-1.5 ${r.ok ? "text-emerald-600" : "text-slate-400"}`}
                >
                  <CheckCircle2 size={12} />
                  {r.label}
                </li>
              ))}
            </ul>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Confirm Password</label>
            <div className="flex items-center rounded-lg border border-slate-300 bg-white transition focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-100">
              <Lock className="ml-3 text-slate-400" size={18} />
              <input
                data-testid="reset-confirm-input"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm password"
                className="w-full bg-transparent px-3 py-3 outline-none"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="px-3 text-slate-500 hover:text-blue-600"
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            data-testid="reset-submit-btn"
            type="submit"
            disabled={loading}
            className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-3 font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Updating...
              </>
            ) : (
              <>
                Update Password
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </form>

        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
          <ShieldCheck size={14} className="text-green-500" />
          Secured with 256-bit encryption
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
